import React, { useEffect, useState, useContext } from 'react';
import { Switch, useHistory } from 'react-router-dom';
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Registration from '../registration';
import SignIn from '../signIn';
import SignOut from '../signOut';
import OrderHistory from '../orderHistoryInfo';
import { MainRoute } from './components/mainRoute';
import { PrivateRoute } from './components/privateRoute';
import { defaultTheme } from '../theme';
import { postMessage } from '../utils/postMessage';
import { WebApiContext } from 'ascendon-web-api';
import validateMessage from './validate-message';
import AccountInfo from '../accountInfo';
import SubscriptionInfo from '../subscriptionInfo';
import Checkout from '../checkout';
import {
  isSessionOrientedMessageEvent,
  readSubscriberCountry,
  sessionAndLogoutEventHandler,
} from '../utils/common';
import { loadStripe } from '@stripe/stripe-js';
import ErrorBoundary from '../common/errorBoundary';
import { ConfigMessageContext } from './configMessageContext';

function App(props) {
  const [stripePromise, setStripePromise] = useState(null);
  const history = useHistory();
  const { i18n } = useTranslation();
  const [profileType, setProfileType] = useState(null);
  const [showAccountMessage, setShowAccountMessage] = useState(null);
  const [showBackButton, setShowBackButton] = useState(null);
  const [leadSource, setLeadSource] = useState(null);
  const [apiUrl, setApiUrl] = useState(null);
  const [securityApiKey, setSecurityApiKey] = useState(null);
  const [staticApiKey, setStaticApiKey] = useState(null);
  const [securityApiUrl, setSecurityApiUrl] = useState(null);
  const [staticApiUrl, setStaticApiUrl] = useState(null);
  const [bypassApigee, setBypassApigee] = useState(null);
  const [cdStripeHeader, setCdStripeHeader] = useState(null);
  const [productExternalReference, setProductExternalReference] =
    useState(null);
  const [productExternalReferenceType, setProductExternalReferenceType] =
    useState(null);
  const [pricingPlanExternalReference, setPricingPlanExternalReference] =
    useState(null);
  const [isQuickCancel, setIsQuickCancel] = useState(false);
  const [, setIsMountedState] = useState(true);

  const WebApi = useContext(WebApiContext);

  useEffect(() => {
    window.addEventListener(
      'message',
      (e) => {
        const url = new URL(e.origin);
        const acceptedOrigin =
          ASCENDON_ENV_CONFIG.acceptedOrigin.findIndex((candidate) =>
            url.hostname.endsWith(candidate),
          ) > -1;

        if (!acceptedOrigin) return;
        if (e.data && typeof e.data === 'string') {
          const ignoreMessage =
            e.data.indexOf('[iFrameSizer]') > -1 ||
            e.data.indexOf('webpackHotUpdate') > -1;

          let message;
          try {
            message = !ignoreMessage ? JSON.parse(e.data) : null;
          } catch (error) {
            console.warn(`Ignoring unparsable message `, e, error);
          }
          const sessionMessage = isSessionOrientedMessageEvent(message);
          sessionAndLogoutEventHandler(sessionMessage, history);
          if (message && !sessionMessage?.isSessionMessage) {
            history.location.pathname = '/';
            const messageParameters = validateMessage(message);
            if (messageParameters.isValid) {
              i18n.changeLanguage(messageParameters.language, () => {
                WebApi.updateSettings({
                  language: messageParameters.language,
                });
                setProfileType(messageParameters.profileType);
                setLeadSource(messageParameters.leadSource);
                setApiUrl(messageParameters.apiUrl);
                setSecurityApiKey(messageParameters.securityApiKey);
                setStaticApiKey(messageParameters.staticApiKey);
                setSecurityApiUrl(messageParameters.securityApiUrl);
                setStaticApiUrl(messageParameters.staticApiUrl);
                setShowBackButton(messageParameters.showBackButton);
                setShowAccountMessage(
                  messageParameters.showAlreadyHaveAccountMessage,
                );
                setProductExternalReference(
                  messageParameters.productExternalReference,
                );
                setProductExternalReferenceType(
                  messageParameters.productExternalReferenceType,
                );
                setPricingPlanExternalReference(
                  messageParameters.pricingPlanExternalReference,
                );
                setBypassApigee(messageParameters.bypassApigee);
                setCdStripeHeader(messageParameters.cdStripeHeader);
                setIsQuickCancel(messageParameters?.isQuickCancel || false);
                history.push(message.page);
              });
            } else {
              messageParameters.errors.forEach((error) => {
                postMessage('module.error', {
                  error: error,
                });
              });
            }
          }
        }
      },
      false,
    );

    history.location.pathname &&
      window.parent.postMessage(
        JSON.stringify({ page: history.location.pathname }),
        '*',
      );

    return () => {
      /**Expected in rtl to clean up the async task i18n.changeLanguage state update. By default cleanup happens automatically in rtl */
      setIsMountedState(false);
    };
  }, [history]);

  useEffect(() => {
    postMessage('app.loaded', null);
    const { publishableKey, stripeBetaOptions } = ASCENDON_ENV_CONFIG?.stripe;
    setStripePromise(loadStripe(publishableKey, { ...stripeBetaOptions }));
  }, []);

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <ConfigMessageContext.Provider
          value={{ isQuickCancel, setIsQuickCancel, bypassApigee }}
        >
          <Switch>
            <MainRoute
              {...props}
              exact
              component={Registration}
              path={['/Registration', '/']}
              profileType={profileType}
              showAlreadyHaveAccountMessage={showAccountMessage}
              showBackButton={showBackButton}
              leadSource={leadSource}
              apiUrl={apiUrl}
              securityApiKey={securityApiKey}
              securityApiUrl={securityApiUrl}
              staticApiKey={staticApiKey}
              staticApiUrl={staticApiUrl}
              bypassApigee={bypassApigee}
            />
            <PrivateRoute
              {...props}
              exact
              component={AccountInfo}
              path={['/my-account']}
              profileType={profileType}
              showBackButton={false}
            />
            <PrivateRoute
              {...props}
              exact
              component={SubscriptionInfo}
              path={['/my-subscription']}
              profileType={profileType}
              showBackButton={false}
              productExternalReference={productExternalReference}
              productExternalReferenceType={productExternalReferenceType}
              stripePromise={stripePromise}
              cdStripeHeader={cdStripeHeader}
              staticApiKey={staticApiKey}
              staticApiUrl={staticApiUrl}
            />
            <PrivateRoute
              {...props}
              exact
              userCountry={readSubscriberCountry()}
              apiUrl={apiUrl}
              staticApiKey={staticApiKey}
              staticApiUrl={staticApiUrl}
              component={Checkout}
              path={['/payment']}
              productExternalReference={productExternalReference}
              productExternalReferenceType={productExternalReferenceType}
              pricingPlanExternalReference={pricingPlanExternalReference}
              stripePromise={stripePromise}
              cdStripeHeader={cdStripeHeader}
            />
            <MainRoute
              {...props}
              exact
              component={SignIn}
              path={['/signIn']}
              profileType={profileType}
              staticApiKey={staticApiKey}
              staticApiUrl={staticApiUrl}
            />
            <MainRoute
              {...props}
              exact
              component={SignOut}
              path={['/signOut']}
              profileType={profileType}
            />
            <PrivateRoute
              {...props}
              exact
              component={OrderHistory}
              path={['/OrderHistory']}
              profileType={profileType}
              productExternalReference={productExternalReference}
              productExternalReferenceType={productExternalReferenceType}
            />
          </Switch>
        </ConfigMessageContext.Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
